// views/Geradoras/Visao3D/usinasAoVivo.ts
//
// Estado "agora" de cada usina, pronto para a Visão 3D desenhar. Sem React e
// sem Supabase — só junta o que o hook buscou, com as MESMAS regras do Painel
// (potência efetiva, status, delta de energia). As duas abas mostram os
// mesmos números por construção, não por coincidência.

import type { UnidadeGeradora, LeituraTelemetria, GrowattMetric, AlarmeGeradora, EventoAlarme } from '../../../types';
import { agruparAlarmes } from '../../../src/lib/alarmesGeradoras.ts';
import {
    getVendor, mapStatus, isEstadoOperando, potenciaEfetivaW, deltaOr, tempoSemReportar,
    getFatorCapacidade, getRotulosCurtos, capacidadeMwpToW,
    type PlantState, type Vendor,
} from '../geradorasShared.ts';

/** Mesma janela do sino de alarmes do Painel: o que é recente o bastante para exigir ação. */
export const JANELA_ALARME_MS = 72 * 3600000;

export interface UsinaAoVivo {
    site: string;
    usina: UnidadeGeradora;
    /** Nome curto, o que cabe em cima da usina na cena. */
    rotulo: string;
    vendor: Vendor;
    estado: PlantState;
    statusCode: number | null;
    potenciaW: number | null;
    /** % da capacidade, 0–100. Null sem potência ou sem MWp cadastrado. */
    fator: number | null;
    prod1dKwh: number | null;
    prod30dKwh: number | null;
    /** Quando a USINA reportou por último — ver getDadoUsinaAt no Painel. */
    dadoAt: string | null;
    semReportar: ReturnType<typeof tempoSemReportar>;
    /** Eventos das últimas 72 h sem fim registrado. */
    alarmesAbertos: EventoAlarme[];
    /**
     * Pede olho: fora de "Operando", ou sem reportar há um dia ou mais mesmo
     * com status bom. Alarme sem fim registrado NÃO entra — há fabricante que
     * nunca avisa o fim, e aí quase toda usina ficaria em atenção por 72 h.
     */
    atencao: boolean;
}

export interface ResumoFrota {
    total: number;
    potenciaW: number;
    reportando: number;
    operando: number;
    parciais: number;
    atencao: number;
    capacidadeMwp: number;
    /** % da capacidade cadastrada que a frota entrega agora. */
    fator: number | null;
    prod1dKwh: number;
}

export interface DadosBrutos {
    unidades: UnidadeGeradora[];
    leituras: Record<string, LeituraTelemetria>;
    metrics: Record<string, GrowattMetric>;
    baselines: Record<string, Record<string, number | null>>;
    alarmes: AlarmeGeradora[];
}

export const derivarUsinas = (
    { unidades, leituras, metrics, baselines, alarmes }: DadosBrutos,
    agora: number = Date.now(),
): { usinas: UsinaAoVivo[]; resumo: ResumoFrota } => {
    // Mesmo dedup do Painel: uma linha por site_id_api, em ordem de nome.
    const porSite = new Map<string, UnidadeGeradora>();
    unidades.forEach(u => porSite.set(u.site_id_api, u));
    const lista = [...porSite.values()].sort((a, b) => a.nome_usina.localeCompare(b.nome_usina, 'pt-BR'));
    const rotulos = getRotulosCurtos(lista);

    const abertosPorSite = new Map<string, EventoAlarme[]>();
    agruparAlarmes(alarmes)
        .filter(e => !e.encerrado && Date.parse(e.inicio) >= agora - JANELA_ALARME_MS)
        .forEach(e => {
            const site = e.representante.site_id_api;
            abertosPorSite.set(site, [...(abertosPorSite.get(site) || []), e]);
        });

    const usinas = lista.map((usina): UsinaAoVivo => {
        const site = usina.site_id_api;
        const gm = metrics[site];
        const vendor = getVendor(site, usina.fabricante);
        const statusCode = gm?.status_code ?? usina.status_atual;
        const estado = mapStatus(vendor, statusCode);
        const potenciaW = potenciaEfetivaW(leituras[site], gm?.power_w);
        const b = baselines[site] || {};
        const latest = b.latest ?? null;
        const alarmesAbertos = abertosPorSite.get(site) || [];
        const semReportar = tempoSemReportar(gm?.last_update_time);
        return {
            site,
            usina,
            rotulo: rotulos[site],
            vendor,
            estado,
            statusCode: statusCode ?? null,
            potenciaW,
            fator: getFatorCapacidade(potenciaW, usina.capacidade_instalada),
            prod1dKwh: deltaOr(latest, b.base_1d, gm?.prod_1d_kwh),
            prod30dKwh: deltaOr(latest, b.base_30d, gm?.prod_30d_kwh),
            dadoAt: gm?.last_update_time || leituras[site]?.data_leitura || null,
            semReportar,
            alarmesAbertos,
            atencao: estado !== 'operando' || !!semReportar?.grave,
        };
    });

    const potenciaW = usinas.reduce((s, u) => s + (u.potenciaW || 0), 0);
    const capacidadeMwp = usinas.reduce((s, u) => s + (u.usina.capacidade_instalada || 0), 0);
    const capW = capacidadeMwpToW(capacidadeMwp);
    return {
        usinas,
        resumo: {
            total: usinas.length,
            potenciaW,
            reportando: usinas.filter(u => u.potenciaW != null).length,
            operando: usinas.filter(u => isEstadoOperando(u.estado)).length,
            parciais: usinas.filter(u => u.estado === 'parcial').length,
            atencao: usinas.filter(u => u.atencao).length,
            capacidadeMwp,
            fator: capW ? Math.min(Math.round((potenciaW / capW) * 100), 100) : null,
            prod1dKwh: usinas.reduce((s, u) => s + (u.prod1dKwh || 0), 0),
        },
    };
};

// ---------------- Relógio da cena ----------------

/**
 * Hora local das usinas (Goiás, fuso de Brasília) como fração 0–24. A luz da
 * maquete segue este relógio, não o do computador de quem olha.
 */
export const horaDasUsinas = (ms: number): number => {
    const partes = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date(ms));
    const parte = (tipo: string) => Number(partes.find(p => p.type === tipo)?.value || 0);
    return parte('hour') + parte('minute') / 60;
};

/** Sol acima do horizonte, em números redondos para a latitude de Goiás. */
export const NASCER_DO_SOL = 5.75;
export const POR_DO_SOL = 18.25;
export const ehDia = (hora: number) => hora >= NASCER_DO_SOL && hora < POR_DO_SOL;
